import React, { useEffect, useState, useCallback } from 'react';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import { ccv2Fetch } from '../../services/callcenterV2';
import { formatDate } from '../../utils/datetime';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

const FLAG_DESCRIPTIONS = {
  ccv2_command_via_event_only: 'Roteamento de comandos de pausa/despause via RabbitMQ (Fase 2)',
  ccv2_schedule_enforcement: 'Verificação de escala de trabalho obrigatória (Fase 3)',
  ccv2_metrics_catalog_enabled: 'Catálogo de métricas configurável (Fase 4)',
  ccv2_call_history_enabled: 'Popula o Histórico de Chamadas via eventos cdr.completed (Fase 4)',
  ccv2_metrics_aggregation_enabled: 'Job de agregação Redis→Postgres hora/dia (Fase 4)',
  ccv2_lgpd_anonymize_enabled: 'Job diário de anonimização LGPD no histórico de chamadas (Fase 4)',
  ccv2_phase5_ui: 'UI avançada — dashboards role-aware, wallboard e telas administrativas (Fase 5)',
  ccv2_short_abandon_enabled: 'Otimização da fila: ignorar abandonos com espera abaixo do limite (segundos)',
};

const NUMERIC_FLAG_KEYS = new Set(['ccv2_short_abandon_enabled']);

const flagKeyOf = (f) => f.flagKey ?? f.flag_key;

function parseSeconds(flag) {
  const raw = flag.description ?? flag.value;
  const parsed = parseInt(String(raw ?? '10'), 10);
  if (Number.isNaN(parsed)) return 10;
  return Math.min(120, Math.max(1, parsed));
}

function FeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [secondsDraft, setSecondsDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ccv2Fetch('/v2/callcenter/feature-flags');
      const list = Array.isArray(data) ? data : (data?.flags || []);
      setFlags(list);
      const drafts = {};
      list.forEach((f) => {
        const key = flagKeyOf(f);
        if (NUMERIC_FLAG_KEYS.has(key)) {
          drafts[key] = parseSeconds(f);
        }
      });
      setSecondsDraft(drafts);
    } catch (e) {
      if (e.status === 404) {
        setFlags([
          { flag_key: 'ccv2_command_via_event_only', enabled: false, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_schedule_enforcement', enabled: false, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_metrics_catalog_enabled', enabled: true, updated_by: null, updated_at: null },
          { flag_key: 'ccv2_phase5_ui', enabled: false, updated_by: null, updated_at: null },
        ]);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persistFlag = useCallback(async (flag, enabled, value) => {
    const key = flagKeyOf(flag);
    const body = { enabled };
    if (value !== undefined && NUMERIC_FLAG_KEYS.has(key)) {
      body.value = value;
    }
    await ccv2Fetch(`/v2/callcenter/feature-flags/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }, []);

  const handleToggle = useCallback(async (flag) => {
    const key = flagKeyOf(flag);
    setToggling(key);
    try {
      const nextEnabled = !flag.enabled;
      const value = NUMERIC_FLAG_KEYS.has(key) ? (secondsDraft[key] ?? parseSeconds(flag)) : undefined;
      await persistFlag(flag, nextEnabled, value);
      setSuccess(`Flag "${key}" ${nextEnabled ? 'ativada' : 'desativada'}.`);
      load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setFlags((prev) => prev.map((f) => flagKeyOf(f) === key ? { ...f, enabled: !f.enabled } : f));
      setSuccess(`Flag "${key}" alterada localmente (endpoint não retornou 200 — verifique backend).`);
      setTimeout(() => setSuccess(null), 4000);
    } finally {
      setToggling(null);
    }
  }, [load, persistFlag, secondsDraft]);

  const handleSecondsSave = useCallback(async (flag) => {
    const key = flagKeyOf(flag);
    const value = secondsDraft[key] ?? parseSeconds(flag);
    setToggling(`${key}:seconds`);
    try {
      await persistFlag(flag, flag.enabled, value);
      setSuccess(`Limite de abandono curto atualizado para ${value}s.`);
      load();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.message || 'Falha ao salvar segundos.');
    } finally {
      setToggling(null);
    }
  }, [load, persistFlag, secondsDraft]);

  return (
    <RequireRole roles={['manager', 'admin', 'root']}>
      <div className="ccv2-page-container ccv2-p5-container">
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Feature Flags</h1>
            <p>Ative ou desative funcionalidades por tenant</p>
          </div>
          <div className="ccv2-page-actions">
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={load}>🔄</button>
          </div>
        </div>

        {success && <div className="ccv2-p5-success">{success}</div>}
        {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

        {loading ? (
          <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
        ) : (
          <div className="ccv2-config-card">
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Descrição</th>
                    <th>Estado</th>
                    <th>Último alterador</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.length === 0 ? (
                    <tr><td colSpan={5} className="ccv2-p5-empty">Nenhuma feature flag encontrada.</td></tr>
                  ) : flags.map((f) => (
                    <tr key={flagKeyOf(f)}>
                      <td><code style={{ fontSize: '0.8rem' }}>{flagKeyOf(f)}</code></td>
                      <td style={{ fontSize: '0.825rem', color: '#475569' }}>
                        {FLAG_DESCRIPTIONS[flagKeyOf(f)] || '—'}
                        {NUMERIC_FLAG_KEYS.has(flagKeyOf(f)) && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.78rem' }}>Segundos:</label>
                            <input
                              type="number"
                              min={1}
                              max={120}
                              value={secondsDraft[flagKeyOf(f)] ?? parseSeconds(f)}
                              onChange={(e) => {
                                const next = parseInt(e.target.value, 10);
                                setSecondsDraft((prev) => ({
                                  ...prev,
                                  [flagKeyOf(f)]: Number.isNaN(next) ? 10 : Math.min(120, Math.max(1, next)),
                                }));
                              }}
                              style={{ width: '4.5rem', padding: '0.25rem 0.4rem' }}
                            />
                            <button
                              className="ccv2-p5-btn ccv2-p5-btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              disabled={toggling === `${flagKeyOf(f)}:seconds`}
                              onClick={() => handleSecondsSave(f)}
                            >
                              Salvar
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`ccv2-p5-badge ${f.enabled ? 'ccv2-p5-badge-active' : 'ccv2-p5-badge-inactive'}`}>
                          {f.enabled ? '✅ Ativo' : '⛔ Inativo'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                        {f.updated_by ? `#${f.updated_by}` : '—'}
                        {f.updated_at && <span style={{ marginLeft: '0.25rem' }}>{formatDate(f.updated_at)}</span>}
                      </td>
                      <td>
                        <button
                          className={`ccv2-p5-btn ${f.enabled ? 'ccv2-p5-btn-danger' : 'ccv2-p5-btn-success'}`}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                          disabled={toggling === flagKeyOf(f)}
                          onClick={() => handleToggle(f)}
                        >
                          {toggling === flagKeyOf(f) ? '⌛' : f.enabled ? '⛔ Desativar' : '✅ Ativar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </RequireRole>
  );
}

export default FeatureFlags;
